// PRIMARY COMPONENT
// =================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import DropdownMenuFilter from '../../components/DropdownMenu/DropdownMenuFilter.component';
import Layout from '../../components/Layout/Layout.component';
import Pagination from '../../components/Pagination.component';
import { SelectableTable } from '../../components/SelectableTable/SelectableTable.component';
import { IndexedStrings, SelectableTableColumn } from '../../components/SelectableTable/SelectableTable.types';
import ViewContainer from '../../components/ViewContainer/ViewContainer.component';
import { defaultPageInfo, IPageInfo } from '../../models/page.types';
import { HeaderCardRepo } from './HeaderCard/HeaderCardRepo.component';
import { useStyles } from './SampleAssessmentRepository.styles';
import FilterListIcon from '@material-ui/icons/FilterList';
import {
  getSampleAssessmentSolution,
  getSampleAssessmentSolutionRGZ,
} from '../../services/scans/examTaskGroupSolution/examTaskGroupSolution.service';
import { useLayoutContext } from '../../Context/Layout.context';
import { SampleAssessmentRGZTable, SampleAssessmentSchoolTable } from './SampleAssessmentRepository.types';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { IconButton } from '@material-ui/core';
import { generateUUid } from '../../utils/generator';
import { getExamCommitteeNameFromId } from './Helpers/AccountTypeRendering';
import { committeesArray } from '../../models/committees.types';
import { generateRGZsampleLink } from './Helpers/generateRGZsampleLink';
import { ExamTaskGroupSolutionAssigmentStateText } from '../../enum/examTaskGroupSolutionAssgmentState';
import { RangeFilter } from '../../components/SelectableTable/filters/RangeFilter.component';
import FilterStateGroup from '../../models/filterStateGroup.types';
import { useExaminer } from '../../Context/Examiner.context';
import _ from 'lodash';
import {
  SampleAssessment,
  SampleAssessmentRGZ,
} from '../../services/scans/examTaskGroupSolution/examTaskGroupSolution.types';
import { statusAssignmentRgz } from './Helpers/StatusRgzElement';
import { getErrorPageUrl, getSampleAssessmentRepositoryUrl } from '../../utils/routerUrls';
import { toTitleCase } from '../../utils/toTitleCase';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { contextualHelpActions } from '../../reducers/contextualHelpReducer/contextualHelpSlice';
import { ContextualHelp } from '../../components/ContextualHelp/ContextualHelp.component';
import { useTranslation } from 'react-i18next';

/**
 * Route: Sample Assessment Repository View.
 */

const SampleAssessmentRepository: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { examiner } = useExaminer();
  const { t } = useTranslation();

  const { showLoader } = useLayoutContext();

  const session = useSelector((state: RootState) => state.assessmentSession.assessmentSession);
  const EmptySchoolFilterSearch = {
    committee: [],
    schoolCode: '',
    countFrom: '',
    countTo: '',
  };
  const { schoolId: schoolParamId } = useParams<{ schoolId: string }>();
  const sessionName = session
    ? `${toTitleCase(session.examName)} (${toTitleCase(session.examSheetName)}-${session.dashboardSheetCode})`
    : '';

  const [pageInfo, setPageInfo] = useState<IPageInfo>(defaultPageInfo);
  const [pageInfoRGZ, setPageInfoRGZ] = useState<IPageInfo>({
    currentPage: 0,
    totalPages: 0,
    pageSize: 100,
    totalCount: 0,
    hasPrevious: false,
    hasNext: false,
  });
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);
  const [sampleAssessmentsData, setSampleAssessmentsData] = useState<SampleAssessment[]>([]);
  const [sampleAssessments, setSampleAssessments] = useState<SampleAssessmentSchoolTable[]>([]);
  const [sampleRGZData, setSampleRGZData] = useState<SampleAssessmentRGZ[]>([]);
  const [sampleRGZ, setSampleRGZ] = useState<SampleAssessmentRGZTable[]>([]);
  const [filterSchoolTable, setFilterSchoolTable] = useState<IndexedStrings>(EmptySchoolFilterSearch);
  const [filterRGZTable, setFilterRGZTable] = useState<IndexedStrings>({
    examineeIdentity: '',
    examTaskGroupSolutionId: '',
    assignmentState: [],
  });
  const [selectedSampleAssessments, setSelectedSampleAssessments] = useState<SampleAssessmentSchoolTable[]>([]);
  const [filterSchoolState, setFilterSchoolState] = useState<FilterStateGroup[]>([]);
  const [filterRgzState, setFilterRgzState] = useState<FilterStateGroup[]>([]);
  const [isFirstView, setIsFirstView] = useState<boolean>(true);
  const checkRGZfilter = useRef<IndexedStrings>(EmptySchoolFilterSearch);
  const [schoolId, setSchoolId] = useState<string>()

  const [activeTabForSimpleNavigation, setActiveTabForSimpleNavigation] = useState<'left' | 'right'>('left');

  const handleRGZTableFilterChange = useCallback(
    (name: string, value: string | string[]) => {
      typeof value === 'string' && value.length === 1 ? setIsValidFilter(false) : setIsValidFilter(true);
      setFilterRGZTable((state) => ({
        ...state,
        [name]: value,
      }));
    },
    [setFilterRGZTable],
  );

  const totalRecord = useRef<number>(0);
  const abortController = useRef<AbortController | null>(null);

  const columnsSchoolTable: SelectableTableColumn[] = [
    {
      id: 'examCommittee',
      type: 'text',
      title: t('sampleRepositorySchools:sampleRepositorySchools.table.examCommittee'),
      filterType: 'filter',
    },
    {
      id: 'schoolCode',
      type: 'text',
      title: t('sampleRepositorySchools:sampleRepositorySchools.table.schoolCode'),
      filterType: 'search',
    },
    {
      id: 'rgzCount',
      type: 'text',
      title: t('sampleRepositorySchools:sampleRepositorySchools.table.rgzCount'),
      filterType: 'filter',
    },
    {
      id: 'action',
      type: 'children',
      title: '',
      filterType: null,
    },
  ];

  const columnsRGZTable: SelectableTableColumn[] = [
    {
      id: 'examineeIdentity',
      type: 'text',
      title: t('sampleRepositoryRGZs:table.examineeIdentity'),
      filterType: 'search',
    },
    {
      id: 'examTaskGroupSolutionId',
      type: 'text',
      title: t('sampleRepositoryRGZs:table.examTaskGroupSolutionId'),
      filterType: 'search',
    },
    {
      id: 'assignmentState',
      type: 'text',
      title: t('sampleRepositoryRGZs:table.assignmentState'),
      filterType: 'filter',
    },
  ];
  const [anchorElExamCommittee, setAnchorElExamCommittee] = useState<SVGSVGElement | HTMLElement | null>(null);
  const [anchorElStatus, setAnchorElStatus] = useState<SVGSVGElement | HTMLElement | null>(null);

  const handleClickExamCommitteeMenu = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if (!anchorElExamCommittee) {
      setAnchorElExamCommittee(event.currentTarget);
    } else setAnchorElExamCommittee(null);
  };
  const handleClickStatusFilter = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if (!anchorElStatus) {
      setAnchorElStatus(event.currentTarget);
    } else setAnchorElStatus(null);
  };

  const [examCommitteeFilter, setExamCommitteeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const handleStatusFilterChange = useCallback(
    (value?: string) => {
      if (value && typeof value === 'string') {
        const val = statusFilter.some((f) => f === value)
          ? statusFilter.filter((f) => f !== value)
          : [...statusFilter, value];
        const valEnum: string[] = val.map((v) => ExamTaskGroupSolutionAssigmentStateText.indexOf(v).toString());
        setStatusFilter(val);
        handleRGZTableFilterChange('assignmentState', valEnum);
      } else {
        setStatusFilter([]);
        handleRGZTableFilterChange('assignmentState', []);
      }
    },
    [statusFilter],
  );

  const isFilterEqual: boolean = _.isEqual(filterRGZTable, checkRGZfilter.current);
  const pageNumber = !isFilterEqual ? 0 : pageInfoRGZ.currentPage;

  const clearRgzTable = () => {
    history.replace(getSampleAssessmentRepositoryUrl());
    handleStatusFilterChange();
    setFilterRGZTable({
      examineeIdentity: '',
      examTaskGroupSolutionId: '',
      assignmentState: [],
    });
    if (!_.isEqual(filterSchoolTable, EmptySchoolFilterSearch)) {
      history.replace(getSampleAssessmentRepositoryUrl());
    }
    setPageInfoRGZ({
      currentPage: 0,
      totalPages: 0,
      pageSize: 100,
      totalCount: 0,
      hasPrevious: false,
      hasNext: false,
    });
    setSampleRGZData([]);
    setSampleRGZ([]);
    setSchoolId(undefined)
  };

  const handleSchoolTableFilterChange = useCallback(
    (name: string, value: string | string[]) => {
      if (typeof value === 'string' && value.length === 1 && name !== 'countFrom' && name !== 'countTo') {
        setIsValidFilter(false);
      } else {
        setIsValidFilter(true);
        clearRgzTable();
        setSelectedSampleAssessments([]);
      }
      setFilterSchoolTable((state) => ({
        ...state,
        [name]: value,
      }));
    },
    [filterSchoolTable],
  );

  const handleExamCommitteeFilterChange = useCallback(
    (value?: string) => {
      if (value && typeof value === 'string') {
        const val = examCommitteeFilter.some((f) => f === value)
          ? examCommitteeFilter.filter((f) => f !== value)
          : [...examCommitteeFilter, value];
        setExamCommitteeFilter(val);

        const valIndex = val.map((i) => {
          const res = committeesArray.find((e) => e.name === i);
          return res ? res.id.toString() : '';
        });
        handleSchoolTableFilterChange('committee', valIndex);
      } else {
        setExamCommitteeFilter([]);
        handleSchoolTableFilterChange('committee', []);
      }
    },
    [examCommitteeFilter],
  );

  const getPages = useCallback(
    (_page?: number, _newPageSize?: number) => {
      clearRgzTable();
      setPageInfo((state) => ({
        ...state,
        currentPage: _page ? _page : 0,
        pageSize: _newPageSize ? _newPageSize : state.pageSize,
      }));
    },
    [filterSchoolTable, pageInfo],
  );
  const filterElements: { [key: string]: JSX.Element } = {
    examCommittee: (
      <>
        <FilterListIcon
          className={examCommitteeFilter.length ? classes.activeFilter : ''}
          onClick={handleClickExamCommitteeMenu}
          aria-controls="ExamCommittee-menu"
          aria-haspopup="true"
        />
        <DropdownMenuFilter
          anchorEl={anchorElExamCommittee}
          setAnchorEl={setAnchorElExamCommittee}
          menuItems={committeesArray.slice(0, -1).map((e) => e.name)}
          hasCheckbox={true}
          checkedItems={examCommitteeFilter}
          onChange={handleExamCommitteeFilterChange}
          uncheckAllMenuItems={handleExamCommitteeFilterChange}
        />
      </>
    ),
    rgzCount: (
      <RangeFilter
        fromValueName="countFrom"
        toValueName="countTo"
        onChange={handleSchoolTableFilterChange}
        resetFilter={filterSchoolTable.countFrom === '' && filterSchoolTable.countTo === ''}
      />
    ),
    assignmentState: (
      <>
        <FilterListIcon
          className={statusFilter.length ? classes.activeFilter : ''}
          onClick={handleClickStatusFilter}
          aria-controls="statusRgz-menu"
          aria-haspopup="true"
        />
        <DropdownMenuFilter
          anchorEl={anchorElStatus}
          setAnchorEl={setAnchorElStatus}
          menuItems={Object.values(ExamTaskGroupSolutionAssigmentStateText)}
          hasCheckbox={true}
          checkedItems={statusFilter}
          onChange={handleStatusFilterChange}
          uncheckAllMenuItems={handleStatusFilterChange}
        />
      </>
    ),
  };

  const filtersRgz = Object.values(filterRGZTable);
  const filtersRgzApplied = filtersRgz.filter((e) => e.length > 0).length > 0 ? true : false;
  const filtersSchool = Object.values(filterSchoolTable);
  const filtersSchoolApplied = filtersSchool.filter((e) => e.length > 0).length > 0 ? true : false;

  const handleCurrentPage = (response: IPageInfo) => {
    const { totalPages, pageSize, totalCount, hasPrevious, hasNext } = response;
    if (totalCount < pageInfo.pageSize) {
      setPageInfo({ currentPage: 0, totalPages, pageSize, totalCount, hasPrevious, hasNext });
    } else {
      setPageInfo(response);
    }
  };

  const handleCurrentPageRGZ = (response: IPageInfo) => {
    const { totalPages, pageSize, totalCount, hasPrevious, hasNext } = response;
    if (totalCount < pageInfo.pageSize) {
      setPageInfoRGZ({ currentPage: 0, totalPages, pageSize, totalCount, hasPrevious, hasNext });
    } else {
      setPageInfoRGZ(response);
    }
  };
  const disableChangesTillResponse = () => {
    setPageInfo((state) => ({
      ...state,
      hasNext: false,
      hasPrevious: false,
    }));
  };

  const getSampleAssessmentData = _.debounce((silent?: boolean, signal?: AbortSignal, isFirstView?: boolean) => {
    const filters = Object.keys(filterSchoolTable).map((key) => {
      if (Array.isArray(filterSchoolTable[key])) {
        return (filterSchoolTable[key] as string[]).join(',');
      }
      return filterSchoolTable[key];
    });
    if (!isValidFilter) return;
    clearRgzTable();
    showLoader(!silent);
    disableChangesTillResponse();
    getSampleAssessmentSolution(signal, pageInfo.pageSize, pageInfo.currentPage, ...(filters as string[]))
      .then((res) => {
        setSampleAssessmentsData(res.data.list);
        handleCurrentPage(res.data.pageInfo);
        if (isFirstView) {
          totalRecord.current = res.data.pageInfo.totalCount;
        }
        showLoader(false);
      })
      .catch((e) => {
        console.warn({ e });
        if (e.message !== 'canceled') {
          history.push(getErrorPageUrl(e?.code ?? 500, 'sampleExaminer'));
        }
        showLoader(false);
      });
  }, 1000);

  const getSampleAssessmentRGZData = _.debounce((silent?: boolean, signal?: AbortSignal, schoolId?: string) => {
    const filters = Object.keys(filterRGZTable).map((key) => {
      if (Array.isArray(filterRGZTable[key])) {
        return (filterRGZTable[key] as string[]).join(',');
      }
      return filterRGZTable[key];
    });
    if (!isValidFilter) return;
    showLoader(true);
    getSampleAssessmentSolutionRGZ(schoolId ? schoolId : schoolParamId, signal, pageInfoRGZ.pageSize, pageNumber, ...(filters as string[]))
      .then((res) => {
        setSampleRGZData(res.data.list);
        handleCurrentPageRGZ(res.data.pageInfo);
        checkRGZfilter.current = filterRGZTable;
        showLoader(false);
      })
      .catch((e) => {
        console.warn({ e });
        if (e.message !== 'canceled') {
          console.warn({ e });
          setSampleRGZData([]);
          showLoader(false);
        }
      });
  }, 1000);

  const getActionsMenuIcon = (se: SampleAssessmentSchoolTable): JSX.Element => {
    return (
      <IconButton
        disableRipple
        style={{ margin: '7px -10px 7px -18px' }}
        size="small"
        aria-label="przycisk do rozwijania listy RGZ"
        onClick={() => {
          history.replace(getSampleAssessmentRepositoryUrl(se.schoolId));
          setSchoolId(se.schoolId)
        }}
      >
        <NavigateNextIcon />
      </IconButton>
    );
  };

  const handleSelectionChange = useCallback(
    (schoolId: string) => {
      const row = sampleAssessments.find((item) => item.schoolId === schoolId);
      if (!row) {
        return;
      }
      setSelectedSampleAssessments([
        {
          id: row.id,
          examCommittee: row.examCommittee,
          schoolId: row.schoolId,
          rgzCount: row.rgzCount,
          schoolCode: row.schoolCode,
        },
      ]);
    },
    [sampleAssessments],
  );

  useEffect(() => {
    if (schoolParamId) {
      handleSelectionChange(schoolParamId);
    }
  }, [schoolParamId]);

  useEffect(() => {
    if (examiner?.id) {
      const abortController = new AbortController();
      const { signal } = abortController;
      if (isFirstView) {
        getSampleAssessmentData(false, signal, isFirstView);
      } else {
        getSampleAssessmentData(true, signal);
      }
      setIsFirstView(false);
      return () => {
        abortController.abort();
      };
    }
  }, [pageInfo.currentPage, pageInfo.pageSize, filterSchoolTable, examiner?.id]);

  useEffect(() => {
    if (schoolId && !isFirstView) {
      abortController.current = new AbortController();
      const { signal } = abortController.current;
      getSampleAssessmentRGZData(true, signal, schoolId);
    }
  }, [filterRGZTable, schoolId, pageInfoRGZ.currentPage, isFirstView]);

  useEffect(() => {
    return () => {
      abortController.current?.abort();
    };
  }, []);

  //convert data for the tables
  useEffect(() => {
    if (sampleAssessmentsData.length) {
      const tableAssessment: SampleAssessmentSchoolTable[] = sampleAssessmentsData.map((sa: SampleAssessment) => ({
        id: sa.schoolId,
        examCommittee: getExamCommitteeNameFromId(sa.examCommittee),
        schoolCode: sa.schoolCode,
        schoolId: sa.schoolId,
        rgzCount: <span style={{ marginLeft: '8px' }}>{sa.rgzCount}</span>,
        action: getActionsMenuIcon({
          id: sa.schoolId,
          examCommittee: <>{sa.examCommittee}</>,
          schoolId: sa.schoolId,
          rgzCount: <>{sa.rgzCount}</>,
          schoolCode: sa.schoolCode,
        }),
      }));
      setSampleAssessments(tableAssessment);
    } else {
      setSampleAssessments([]);
    }
  }, [sampleAssessmentsData, pageInfo.currentPage, pageInfo.pageSize]);

  useEffect(() => {
    if (sampleRGZData.length && examiner && examiner.id) {
      const tableRgz: SampleAssessmentRGZTable[] = sampleRGZData.map((rgz: SampleAssessmentRGZ) => ({
        id: generateUUid(rgz.examTaskGroupSolutionId),
        examTaskGroupSolutionId: generateRGZsampleLink(rgz.schoolId, rgz.examTaskGroupSolutionId, examiner.id),
        schoolId: rgz.schoolId,
        examineeId: rgz.examineeId,
        examineeIdentity: <span style={{ marginLeft: '10px' }}>{rgz.examineeIdentity}</span>,
        assignmentState: statusAssignmentRgz(rgz.assignmentState),
      }));
      setSampleRGZ(tableRgz);
    } else {
      setSampleRGZ([]);
    }
  }, [sampleRGZData, examiner?.id]);

  useEffect(() => {
    if (examCommitteeFilter.length > 0 || filterSchoolTable.countFrom || filterSchoolTable.countTo) {
      const filterState: FilterStateGroup[] = [];
      if (examCommitteeFilter.length > 0) {
        filterState.push({ name: 'Komisja:', checkedItems: examCommitteeFilter });
      }
      if (filterSchoolTable.countFrom) {
        filterState.push({ name: 'Od:', checkedItems: [filterSchoolTable.countFrom.toString()] });
      }
      if (filterSchoolTable.countTo) {
        filterState.push({ name: 'Do:', checkedItems: [filterSchoolTable.countTo.toString()] });
      }
      setFilterSchoolState(filterState);
    } else {
      setFilterSchoolState([]);
    }
  }, [sampleAssessments, filterSchoolTable, filtersSchoolApplied, examCommitteeFilter]);

  useEffect(() => {
    if (statusFilter.length > 0) {
      const filterState: FilterStateGroup[] = [];
      filterState.push({ name: 'Status:', checkedItems: statusFilter });
      setFilterRgzState(filterState);
    } else {
      setFilterRgzState([]);
    }
  }, [sampleRGZ, filterSchoolTable, filtersRgzApplied, statusFilter]);

  const uncheckAllSchoolFilters = () => {
    setFilterSchoolState([]);
    setExamCommitteeFilter([]);
    handleExamCommitteeFilterChange();
    setFilterSchoolTable({ committee: [], schoolCode: '', countFrom: '', countTo: '' });
  };

  const uncheckAllRgzFilters = () => {
    setFilterRgzState([]);
    setStatusFilter([]);
    handleStatusFilterChange();
    setFilterRGZTable({ examineeIdentity: '', examTaskGroupSolutionId: '', assignmentState: [] });
  };

  const uncheckSchoolFilterItem = (item?: string) => {
    if (item) {
      const element = filterSchoolState.find((e) => e.checkedItems.find((x) => x === item));
      if (element?.name === 'Komisja:') {
        handleExamCommitteeFilterChange(item);
      } else if (element?.name === 'Od:') {
        handleSchoolTableFilterChange('countFrom', '');
      } else {
        handleSchoolTableFilterChange('countTo', '');
      }
    }
  };

  const uncheckRgzFilterItem = (item?: string) => {
    if (item) {
      handleStatusFilterChange(item);
    }
  };

  const handleOnScroll = (endReached: boolean) => {
    if (endReached) {
      if (pageInfoRGZ.hasNext) {
        setPageInfoRGZ((state) => ({ ...state, currentPage: pageInfoRGZ.currentPage++ }));
        getSampleAssessmentRGZData(true);
      }
    }
  };

  const dispatch = useDispatch();

  const isContextualHelp = useSelector((state: RootState) => state.contextualHelp.isContextualHelp);

  const [activatedContextualHelp, setActivatedContextualHelp] = useState<number | undefined>();
  const tableBar = 8;

  useEffect(() => {
    dispatch(contextualHelpActions.toogleContextualHelp(false));
  }, []);

  useEffect(() => {
    const tableMenu = document.getElementById('tableMenu');
    const contextualHelp8 = document.getElementById('contextualHelp-8');

    if (tableMenu && contextualHelp8) {
      contextualHelp8.style.left = tableMenu.offsetWidth * 2 + 'px';
      contextualHelp8.style.top = tableMenu.getBoundingClientRect().top - contextualHelp8.offsetHeight - 5 + 'px';
    }
  }, [isContextualHelp]);

  const handleGlobalKeyDown = useCallback((e) => {
    const { key } = e;
    switch (key) {
      case 'ArrowLeft':
        if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
          setActiveTabForSimpleNavigation('left');
        }
        break;
      case 'ArrowRight':
        if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
          setActiveTabForSimpleNavigation('right');
        }
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [handleGlobalKeyDown]);

  return (
    <React.Fragment>
      <Layout viewType="sampleExaminer">
        {examiner && (
          <ViewContainer>
            <HeaderCardRepo sessionName={sessionName} />
            <span className={classes.pagination} style={{ marginRight: '20px' }}>
              <Pagination
                pageInfo={pageInfo}
                getPage={getPages}
                recordsFound={totalRecord.current}
                filterActive={filtersSchoolApplied}
              />
            </span>
            <span style={{ margin: '100px 5px 0 5px' }}>
              <div className={classes.tablesContainer}>
                <div className={classes.table} style={{ marginLeft: '20px' }}>
                  <SelectableTable<SampleAssessmentSchoolTable>
                    columns={columnsSchoolTable}
                    rows={sampleAssessments}
                    onFilterChange={handleSchoolTableFilterChange}
                    filter={filterSchoolTable}
                    filterElements={filterElements}
                    isMultiChoice={false}
                    withoutEmptyMessage={true}
                    noOverflowInitial={true}
                    oddTableDoesNotChangeColor={true}
                    filterSearch={filtersSchoolApplied && !sampleAssessments.length}
                    filterbar={filterSchoolState}
                    closeFilterItem={uncheckSchoolFilterItem}
                    uncheckAllFilters={uncheckAllSchoolFilters}
                    caption="Repozytorium RGZ - Komisje"
                    simpleNavigatingDisabled={activeTabForSimpleNavigation !== 'left'}
                    selectedItemId={selectedSampleAssessments[0]?.id}
                  />
                </div>
                <div id="tableMenu" className={classes.tableLeft} style={{ marginRight: '48px' }}>
                  {isContextualHelp && (
                    <div style={{ position: 'relative', left: '95%', top: '-20px' }}>
                      <ContextualHelp
                        id={tableBar}
                        isActive={activatedContextualHelp === tableBar}
                        onClick={setActivatedContextualHelp}
                        placement={'left'}
                      />
                    </div>
                  )}
                  <SelectableTable<SampleAssessmentRGZTable>
                    columns={columnsRGZTable}
                    rows={sampleRGZ}
                    onFilterChange={handleRGZTableFilterChange}
                    filter={filterRGZTable}
                    filterElements={filterElements}
                    isMultiChoice={false}
                    withoutEmptyMessage={true}
                    noOverflowInitial={true}
                    oddTableDoesNotChangeColor={true}
                    grayBodyCell={true}
                    filterSearch={filtersRgzApplied && !filterRGZTable.length}
                    headerDisabled={!schoolId}
                    filterbar={filterRgzState}
                    closeFilterItem={uncheckRgzFilterItem}
                    uncheckAllFilters={uncheckAllRgzFilters}
                    onScrollTable={handleOnScroll}
                    caption={'Repozytorium RGZ'}
                    simpleNavigatingDisabled={activeTabForSimpleNavigation !== 'right'}
                  />
                </div>
              </div>
            </span>
          </ViewContainer>
        )}
      </Layout>
    </React.Fragment>
  );
};

export default SampleAssessmentRepository;
